import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'stocker-theme';

const ThemeContext = createContext({
  theme: 'dark',
  isDark: true,
  setTheme: () => {},
  toggleTheme: () => {},
});

const getInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage can throw in locked-down environments (private browsing, etc.) -- fall
    // through to the dark default below instead of crashing the app.
  }
  // Stocker's established default look is dark, so only opt into light via an explicit
  // in-app toggle. Previously fell back to the OS's prefers-color-scheme here, which
  // isn't an explicit signal from the user to this site -- it silently overrode the
  // stated dark default for anyone whose system theme happened to be light, showing a
  // plain white register/login page instead of the intended dark brand look.
  return 'dark';
};

/**
 * Global light/dark theme provider. Drives Tailwind's `dark:` variant (tailwind.config.js
 * has `darkMode: "class"`) by toggling a `dark` class on <html> -- components opt in by
 * pairing every dark-only utility class with a light-mode default, e.g.
 * `bg-white dark:bg-gray-900`, rather than hardcoding one theme.
 *
 * public/index.html has a small inline script that applies this same class before React
 * mounts, so there's no flash of the wrong theme on first paint -- keep both in sync.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Non-fatal: theme just won't persist across reloads in this environment.
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'light' ? 'light' : 'dark');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
